import type { Product, User } from 'payments-api/model';
import { InvoiceLineItemType } from 'payments-api/model/invoiceLineItemType';
import React, { useMemo } from 'react';

import type { CurrencyCode } from '@fragment/types/currencyCodes';
import { currencyPrecision as currencyPrecisionMap } from '@fragment/types/currencyCodes';
import { CurrencyField } from '@fragment/ui/src/components/Payments/CurrencyField';
import {
  LayoutCell,
  LayoutRow,
  TextCell,
} from '@fragment/ui/src/components/Payments/LayoutRow';

import type { ItemTableItem } from './ItemTable';
import { ItemTable } from './ItemTable';

export type InvoiceTotals = {
  totalAmount: { amount: number; currency: CurrencyCode }[];
  isTotalBalanced: boolean;
};

export const getTotalsFromItems = (items: ItemTableItem[]): InvoiceTotals => {
  const totalPayinAmount = items
    .filter((item) => item.type === InvoiceLineItemType.payin)
    .reduce((acc, item) => {
      const code = item.currencyCode as CurrencyCode;
      acc[code] = (acc[code] || 0) + Number(item.amount);
      return acc;
    }, {} as Record<CurrencyCode, number>);

  const totalPayoutAmount = items
    .filter((item) => item.type === InvoiceLineItemType.payout)
    .reduce((acc, item) => {
      const code = item.currencyCode as CurrencyCode;
      acc[code] = (acc[code] || 0) + Number(item.amount);
      return acc;
    }, {} as Record<CurrencyCode, number>);

  const allCurrencies = new Set([
    ...Object.keys(totalPayinAmount),
    ...Object.keys(totalPayoutAmount),
  ]);
  const isTotalBalanced = Array.from(allCurrencies).every(
    (currency) =>
      (totalPayinAmount[currency as CurrencyCode] || 0) ===
      (totalPayoutAmount[currency as CurrencyCode] || 0)
  );

  const totalAmount = Object.entries(totalPayinAmount).map(
    ([currency, amount]) => ({
      amount: Number(amount),
      currency: currency as CurrencyCode,
    })
  );

  return { totalAmount, isTotalBalanced };
};

/**
 * Totals for the amount row when viewing as a specific user.
 * - If user has both payin and payout: total = payin - payout per currency.
 * - Otherwise: total = absolute value of payin total or payout total per currency.
 */
export const getTotalsForUserView = (
  filteredItems: ItemTableItem[]
): {
  amount: number;
  currency: CurrencyCode;
  payinSum: number;
  payoutSum: number;
}[] => {
  const totalPayin = filteredItems
    .filter((item) => item.type === InvoiceLineItemType.payin)
    .reduce((acc, item) => {
      const code = item.currencyCode as CurrencyCode;
      acc[code] = (acc[code] || 0) + Number(item.amount);
      return acc;
    }, {} as Record<CurrencyCode, number>);

  const totalPayout = filteredItems
    .filter((item) => item.type === InvoiceLineItemType.payout)
    .reduce((acc, item) => {
      const code = item.currencyCode as CurrencyCode;
      acc[code] = (acc[code] || 0) + Number(item.amount);
      return acc;
    }, {} as Record<CurrencyCode, number>);

  const hasPayin = filteredItems.some(
    (item) => item.type === InvoiceLineItemType.payin
  );
  const hasPayout = filteredItems.some(
    (item) => item.type === InvoiceLineItemType.payout
  );
  const hasBoth = hasPayin && hasPayout;

  const currencies = new Set<CurrencyCode>([
    ...(Object.keys(totalPayin) as CurrencyCode[]),
    ...(Object.keys(totalPayout) as CurrencyCode[]),
  ]);

  return Array.from(currencies).map((currency) => {
    const payinSum = totalPayin[currency] || 0;
    const payoutSum = totalPayout[currency] || 0;
    const amount = hasBoth
      ? payinSum - payoutSum
      : Math.abs(payinSum || payoutSum);
    return { amount, currency, payinSum, payoutSum };
  });
};

const filterItemsByUser = (
  items: ItemTableItem[],
  selectedUserId: string
): ItemTableItem[] =>
  selectedUserId === 'all'
    ? items
    : items.filter((item) => item.user_id === selectedUserId);

type InvoiceItemsSectionProps = {
  items: ItemTableItem[];
  products: Product[];
  users: User[];
  selectedUserId: string;
  onAddItem?: (item: Omit<ItemTableItem, 'id'>) => void;
  onUpdateItem?: (id: string, item: Omit<ItemTableItem, 'id'>) => void;
  onRemoveItem?: (id: string) => void;
  onAllocateItem?: (itemId: string) => void;
  showUnbalancedWarning?: boolean;
  children?: React.ReactNode;
};

export const InvoiceItemsSection = ({
  items,
  products,
  users,
  selectedUserId,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  onAllocateItem,
  showUnbalancedWarning = true,
  children,
}: InvoiceItemsSectionProps): JSX.Element => {
  const filteredItems = filterItemsByUser(items, selectedUserId);

  const isEditable =
    onAddItem !== undefined ||
    onUpdateItem !== undefined ||
    onRemoveItem !== undefined;

  const { isTotalBalanced } = useMemo(() => getTotalsFromItems(items), [items]);

  const totalAmountForUserView = useMemo(
    () => getTotalsForUserView(filteredItems),
    [filteredItems]
  );

  const canEdit = selectedUserId === 'all';
  const showTotalAmountRow = selectedUserId !== 'all';

  return (
    <>
      <LayoutRow height="medium" justify="start" hide={showTotalAmountRow}>
        <TextCell
          text="Total Amounts"
          textSize="md"
          align="left"
          border="right"
          textType="secondary"
        />
        <LayoutCell width="full" border="none" align="top">
          {totalAmountForUserView.map((amount) => (
            <CurrencyField
              key={`payin-${amount.currency}`}
              amount={amount.payinSum}
              currencyPrecision={currencyPrecisionMap[amount.currency]}
              currencyCode={amount.currency}
              textSize="md"
              align="left"
              label="Payin"
            />
          ))}
          {totalAmountForUserView.map((amount) => (
            <CurrencyField
              key={`payout-${amount.currency}`}
              amount={amount.payoutSum}
              currencyPrecision={currencyPrecisionMap[amount.currency]}
              currencyCode={amount.currency}
              textSize="md"
              align="left"
              label="Payout"
            />
          ))}
        </LayoutCell>
      </LayoutRow>
      <LayoutRow height="medium" justify="start" hide={!showTotalAmountRow}>
        <TextCell
          text="Amount"
          textSize="md"
          align="left"
          border="right"
          textType="secondary"
        />
        <LayoutCell border="none" align="top" width="full">
          {totalAmountForUserView.map((amount) => (
            <CurrencyField
              key={amount.currency}
              amount={Math.abs(amount.amount)}
              currencyPrecision={currencyPrecisionMap[amount.currency]}
              currencyCode={amount.currency}
              textSize="md"
              align="left"
            />
          ))}
        </LayoutCell>
      </LayoutRow>
      <LayoutRow
        height="small"
        justify="start"
        hide={!(showUnbalancedWarning && !isTotalBalanced)}
      >
        <TextCell
          text="Errors"
          textSize="md"
          align="left"
          border="right"
          textType="secondary"
        />
        <TextCell
          text="Invoice unbalanced - Pay ins ≠ Pay outs"
          textSize="md"
          fullWidth
          align="left"
          border="none"
          textType="warning"
        />
      </LayoutRow>

      <ItemTable
        data-testid="payin-items-table"
        items={filteredItems}
        products={products}
        users={users}
        onAddItem={canEdit ? onAddItem : undefined}
        onUpdateItem={canEdit ? onUpdateItem : undefined}
        onRemoveItem={canEdit ? onRemoveItem : undefined}
        onAllocateItem={onAllocateItem}
      />

      {children}
    </>
  );
};
